import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import SignOutIcon from "../../assets/icons/sign_out.svg";
import sync_icon from "../../assets/images/header/sync-blue.gif";
import searchIcon from "../../assets/icons/searchIcon.svg";
import BellIcon from "../../assets/images/navbar/Bell.svg";
import HamburgerIcon from "../../assets/images/navbar/HamburgerIcon.svg";
import CrossIcon from "../../assets/images/navbar/Cross.svg";
// import NotificationModal from "../../components/App/Modals/Client/NotificationModal";
import { ImportContext } from "../../Context/ImportContext";
import OnboardingContext from "../../Context/OnboardingContext";
import mkaegoodIcon from "../../assets/images/navbar/MakeGoodIcon.svg";
import makegoodText from "../../assets/images/navbar/MakeGoodText.svg";
import * as API from "../../utils/api";
import Emitter from "../../utils/event-emitter";
import styles from "./AppClientHeader.module.css";
import { useNavContext } from "../../Context/NavContext";
import GroupedSelect from "../GroupSelect";
import { getDollarAmount } from "../../utils/helpers";
import { COMPANY_SETTINGS, NAV_ITEMS, INVOICE } from "../Clients/ClientsNav";
import logout from "../../assets/images/navbar/logout.svg";
import iconDown from "../../assets/images/navbar/downGrey.svg";
import iconUp from "../../assets/images/navbar/upGrey.svg";
import NotificationsModal from "../../_component/ClientsDashBoard/noticationsModal/noticationsModal";
import QBConnectedIcon from "../../assets/icons/QBConnectedIcon.svg";
import QBNotConnectedIcon from "../../assets/icons/QBNotconnectedIcon.svg";
import LastSynced from "../../assets/icons/LastSynced.svg";
import ViewDetails from "../../assets/icons/ViewDetails.svg";

const formatQbRelativeSync = (qb) => {
  if (!(qb && typeof qb === "object")) return "—";

  const fields = [
    "last_synced_at",
    "last_sync_at",
    "last_sync",
    "last_sync_date",
    "lastSyncDate",
  ];
  const raw = fields.map((field) => qb[field]).find((val) => val != null);
  if (!raw) return "—";

  let time = typeof raw === "number" ? (raw < 1e12 ? raw * 1000 : raw) : raw;
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return "—";

  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return "—";
  const sec = Math.floor(diffMs / 1000);

  if (sec < 45) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AppClientHeader = (props) => {
  const { user, disableNav, hideHeadNavOpt } = props;
  const isDepositUser = user?.type === "DepositUser";
  const { progressPercent } = useContext(OnboardingContext);
  const isNavEnabled = isDepositUser || Math.floor(progressPercent) >= 88;
  const homePath = isDepositUser ? "/sales_user/payments" : "/client/dashboard";
  const navigate = useNavigate();
  const { isPolling } = useContext(ImportContext);
  const { selectedNavItem, setSelectedNavItem } = useNavContext();
  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupedOptions, setGroupedOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [showListCountNumber, setShowListCountNumber] = useState(0);
  const [rowAction, setRowAction] = useState([]);
  const debounceTimer = useRef(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);
  const [isInvoiceExpanded, setIsInvoiceExpanded] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [qbData, setQbData] = useState(null);
  const [qbPopoverOpen, setQbPopoverOpen] = useState(false);
  const focusRef = useRef(null);
  const qbPopoverRef = useRef(null);

  useEffect(() => {
    const updateRowActions = () => {
      const baseActions = [
        {
          title: disableNav ? "" : getFullName(user),
          dataBehavior: "user-profile",
          icon: (
            <div className={styles.userAvatar}>{getUserInitials(user)}</div>
          ),
        },
        {
          title: "Notifications",
          dataBehavior: "notifications",
          icon: (
            <div
              className={styles.iconContainer}
              onClick={() => setIsNotificationModalOpen(true)}
            >
              {showListCountNumber > 0 && (
                <div className={styles.notificationCount}>
                  {showListCountNumber}
                </div>
              )}
              <img src={BellIcon} width='20' height='20' alt='Notifications' />
            </div>
          ),
        },
        {
          title: "Sign Out",
          dataBehavior: "sign-out",
          icon: <img src={SignOutIcon} width='20' height='20' alt='Sign Out' />,
        },
      ];

      const filteredActions = isDepositUser
        ? baseActions.filter((a) => a.dataBehavior !== "notifications")
        : baseActions;
      setRowAction(hideHeadNavOpt ? [baseActions[2]] : filteredActions);
    };

    const handleNotificationAlert = (isActive) => {
      setShowListCountNumber(isActive ? notificationList.length : 0);
    };

    Emitter.on("NOTIFICATION_ALERT", handleNotificationAlert);
    updateRowActions();

    return () => {
      Emitter.off("NOTIFICATION_ALERT", handleNotificationAlert);
    };
  }, [disableNav, user, notificationList.length, hideHeadNavOpt]);

  const getQbData = useCallback(async () => {
    try {
      const data = await API.getOrgDetailsMini(user);
      if (data.status_code === 200) {
        const qb = data?.data?.quickbooks ?? data?.quickbooks;
        setQbData(qb);
      }
    } catch (error) {
      throw error;
    }
  }, [user]);

  useEffect(() => {
    getQbData();
  }, [getQbData]);

  useEffect(() => {
    if (searchTerm) {
      handleSearchInput(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setIsInputFocused(false);
        setOpenDropdown(false);
        setOptionsLoading(false);
        focusRef.current?.blur();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!qbPopoverOpen) return;
    const handleClickOutside = (event) => {
      if (
        qbPopoverRef.current &&
        !qbPopoverRef.current.contains(event.target)
      ) {
        setQbPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [qbPopoverOpen]);

  const handleSearchInput = (value) => {
    setSearchTerm(value);
    setOptionsLoading(true);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchBarAPI(value);
    }, 1500);
  };

  const transformData = (data) => {
    const categories = [
      {
        label: "Payments",
        key: "payments",
        valueKey: "amount",
        labelKey: "display_name",
      },
      {
        label: "Accounts",
        key: "accounts",
        valueKey: "id",
        labelKey: "display_name",
        labelKey2: "email",
      },
      {
        label: "Invoices",
        key: "invoices",
        valueKey: "id",
        labelKey: "invoice_number",
      },
      {
        label: "Credit Memos",
        key: "credit_memos",
        valueKey: "credit_memo_number",
        labelKey: "credit_memo_number",
      },
    ];

    const formatAddress1 = (item) => {
      const part1 = [item.display_name, item.email, item.phone_number]
        .filter(Boolean)
        .join("- ");

      return part1;
    };

    const formatAddress2 = (item) => {
      const part2 = [
        item.street_address_primary,
        item.street_address_secondary,
        item.state,
        item.zipcode,
      ]
        .filter(Boolean)
        .join(", ");

      return part2;
    };

    const flattenAccountsForSearch = (nodes) => {
      const out = [];
      const walk = (nodeList, depth, rootDisplayName) => {
        for (const item of nodeList || []) {
          const root =
            depth === 0 ? (item.display_name || "").trim() : rootDisplayName;

          let primaryLabel;
          if (depth === 0) {
            primaryLabel = formatAddress1(item);
          } else if (depth === 1) {
            primaryLabel = `${root} : ${(item.display_name || "").trim()}`;
          } else {
            primaryLabel = `${root} : ... : ${(item.display_name || "").trim()}`;
          }

          const label = `${primaryLabel} AddressString: ${formatAddress2(item)}`;
          out.push({
            id: item.id,
            value: item.id,
            label,
            depth,
          });

          const subs = item.sub_accounts;
          if (Array.isArray(subs) && subs.length > 0) {
            walk(subs, depth + 1, root);
          }
        }
      };
      walk(Array.isArray(nodes) ? nodes : [], 0, null);
      return out;
    };

    return categories.map((category) => {
      const raw = data[category.key];
      const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

      const options =
        category.label === "Accounts"
          ? flattenAccountsForSearch(items)
          : items.map((item) => {
              return {
                id:
                  category.label === "Payments"
                    ? (item.invoice_id ?? item.id)
                    : (item.id ?? item.user_id),
                value: item[category.valueKey],
                label:
                  category.label === "Payments"
                    ? `${item[category.labelKey]} - ${getDollarAmount(
                        item[category.valueKey]
                      )}`
                    : item[category.labelKey],
              };
            });

      return {
        label: category.label,
        options,
      };
    });
  };

  const searchBarAPI = useCallback(
    (query) => {
      const trimmedQuery = query?.trim();
      if (!trimmedQuery) {
        setGroupedOptions([]);
        // setIsInputFocused(false);
        return;
      }

      setOptionsLoading(true);
      API.searchByEverything(user, trimmedQuery)
        .then((res) => setGroupedOptions(transformData(res)))
        .catch(() => {})
        .finally(() => setOptionsLoading(false));
    },
    [user]
  );

  const handleClearSearch = () => {
    setSearchTerm("");
    setGroupedOptions([]);
    setIsInputFocused(false);
  };

  const renderSearchBar = () => {
    const isMobile = window.innerWidth <= 768;

    return (
      <>
        {isMobile && !isSearchExpanded ? (
          <div
            style={{
              float: "right",
              position: "relative",
              width: isMobile
                ? isSearchExpanded
                  ? "100%"
                  : ""
                : isInputFocused || groupedOptions.length > 0
                  ? "640px"
                  : "190px",
              transition: "width 0.3s ease-in-out",
            }}
          >
            <div className={styles.searchIconWrap}>
              <img
                src={searchIcon}
                className={styles.pointerCursor}
                width='18'
                height='18'
                onClick={() => setIsSearchExpanded(true)}
              />
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: isInputFocused ? "640px" : "190px",
                transition: "width 0.3s ease-in-out",
                position: "relative",
              }}
            >
              {!isNotificationModalOpen && (
                <img
                  src={searchIcon}
                  className={styles.search}
                  width='18'
                  height='18'
                />
              )}

              {/* Cross */}
              {(isInputFocused ||
                groupedOptions.length > 0 ||
                searchTerm.length !== 0) && (
                <img
                  src={CrossIcon}
                  className={styles.crossIcon}
                  width='18'
                  height='18'
                  onClick={() => {
                    handleClearSearch();
                    if (isMobile) setIsSearchExpanded(false);
                  }}
                />
              )}
              <input
                ref={focusRef}
                type='text'
                placeholder='Search'
                value={searchTerm}
                className={styles.inputSearch}
                onFocus={() => {
                  setOpenDropdown(true);
                  setIsInputFocused(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setOpenDropdown(false);
                    //setIsInputFocused(false);
                    if (isMobile) setIsSearchExpanded(false);
                  }, 200);
                }}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
            </div>
            <div ref={inputRef}>
              {isInputFocused && (
                <GroupedSelect
                  options={groupedOptions}
                  label='Choose an Item'
                  searchTerm={searchTerm}
                  openDropdown={openDropdown}
                  placeholder='Search by everything'
                  onChange={handleSelection}
                  optionsLoading={optionsLoading}
                />
              )}
            </div>
          </>
        )}
      </>
    );
  };

  const handleSelection = (group, selectedValue) => {
    setSearchTerm("");

    if (!selectedValue?.id) return; // Guard clause to prevent errors

    const routes = {
      Payments: `/client/invoices/${selectedValue.id}`,
      Invoices: `/client/invoices/${selectedValue.id}`,
      "Credit Memos": `/client/creditMemo/${selectedValue.id}`,
      Accounts: `/client/accounts/${selectedValue.id}`,
    };

    if (routes[group]) {
      navigate(routes[group], { replace: true });
      handleClearSearch();
    }
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const renderMobileDrawer = () => (
    <div className={`${styles.drawer} ${isDrawerOpen ? styles.open : ""}`}>
      <div className={styles.drawerHeader}>
        <Link
          to={homePath}
          className={styles.mobileIcons}
          title={isDepositUser ? "Payments" : "Dashboard"}
          style={{
            pointerEvents: isNavEnabled ? "auto" : "none",
            opacity: isNavEnabled ? "1" : "0.5",
          }}
          onClick={toggleDrawer}
        >
          <img src={mkaegoodIcon} alt='MakeGood' />
          <img src={makegoodText} alt='MakeGood' />
        </Link>
        <button className={styles.closeButton} onClick={toggleDrawer}>
          ✕
        </button>
      </div>
      <div className={styles.drawerWrap}>
        <div className={styles.drawerContent}>
          {(isDepositUser
            ? NAV_ITEMS.filter((i) => i.text === "Payments").map((i) => ({
                ...i,
                path: i.path.replace("/client/", "/sales_user/"),
              }))
            : NAV_ITEMS
          ).map((item) => (
            <React.Fragment key={item.text}>
              {/* Render the actual nav link */}
              <Link
                to={item.path}
                className={`${
                  selectedNavItem === item.text
                    ? styles.active
                    : styles.drawerLink
                }`}
                style={{
                  pointerEvents: isNavEnabled ? "auto" : "none",
                  opacity: isNavEnabled ? "1" : "0.5",
                }}
                onClick={() => {
                  setSelectedNavItem(item.text);
                  toggleDrawer();
                }}
              >
                <img
                  src={
                    selectedNavItem === item.text ? item.iconLite : item.icon
                  }
                  alt={item.text}
                  className={styles.drawerIcon}
                />
                <span>{item.text}</span>
              </Link>

              {/* Inject INVOICE section after "Accounts" */}
              {item.text === "Accounts" && (
                <div className={styles.companySettingsSection}>
                  <div
                    className={styles.drawerLink}
                    onClick={() => setIsInvoiceExpanded(!isInvoiceExpanded)}
                  >
                    <div className={styles.companyWrap}>
                      <img
                        src={INVOICE.icon}
                        alt={INVOICE.text}
                        className={styles.drawerIcon}
                      />
                      <span>{INVOICE.text}</span>
                    </div>
                    <img
                      src={isInvoiceExpanded ? iconDown : iconUp}
                      alt='Toggle'
                    />
                  </div>

                  {isInvoiceExpanded && (
                    <div className={styles.subLinksContainer}>
                      {INVOICE.subLink.map((subLink) => (
                        <Link
                          key={subLink.text}
                          to={subLink.path}
                          className={styles.drawerSubLink}
                          onClick={toggleDrawer}
                        >
                          {subLink.text}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className={styles.drawerFooter}>
          {!isDepositUser && (
            <div
              className={styles.companySettingsSection}
              style={{
                pointerEvents: isNavEnabled ? "auto" : "none",
                opacity: isNavEnabled ? "1" : "0.5",
              }}
            >
              <div
                className={styles.drawerLink}
                onClick={() => setIsCompanyExpanded(!isCompanyExpanded)}
              >
                <div className={styles.companyWrap}>
                  <img
                    src={COMPANY_SETTINGS.icon}
                    alt={COMPANY_SETTINGS.text}
                    className={styles.drawerIcon}
                  />
                  <span>{COMPANY_SETTINGS.text}</span>
                </div>
                <img src={isCompanyExpanded ? iconDown : iconUp} alt='Toggle' />
              </div>

              {isCompanyExpanded && (
                <div className={styles.subLinksContainer}>
                  {COMPANY_SETTINGS.subLinks.map((subLink) => (
                    <Link
                      key={subLink.text}
                      to={subLink.path}
                      className={styles.drawerSubLink}
                      onClick={toggleDrawer}
                    >
                      {subLink.text}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          <Link
            className={`${
              selectedNavItem === "Logout" ? styles.active : styles.drawerLink
            }`}
            to='/sign-out'
            onClick={toggleDrawer}
            style={{ marginBottom: "1rem" }}
          >
            <img
              src={logout}
              className={styles.icon}
              width={20}
              height={20}
              alt='Logout'
            />
            <span>Logout</span>
          </Link>
        </div>
      </div>
    </div>
  );
  return (
    <>
      <header
        className={styles.header}
        style={{ pointerEvents: props.disableNav ? "none" : "auto" }}
      >
        {(!isMobile || !isSearchExpanded) && (
          <>
            <div
              className={styles.hamburgerIconContainer}
              onClick={toggleDrawer}
            >
              <img src={HamburgerIcon} height={20} width={20} alt='Menu' />
            </div>
            <p className={styles.moduleText}>{selectedNavItem}</p>
          </>
        )}
        <div className={styles.headerContent}>
          {!isDepositUser && (
            <div style={{ width: "100%" }}>{renderSearchBar()}</div>
          )}
          {(!isMobile || !isSearchExpanded) && (
            <>
              {!isDepositUser && (
                <div
                  className={styles.bellIconContainer}
                  onClick={() => setIsNotificationModalOpen(true)}
                >
                  <img src={BellIcon} height={20} width={20} />
                  <div
                    style={{
                      backgroundColor: "#E15A45",
                      borderRadius: "40px",
                      width: "7px",
                      height: "7px",
                      position: "absolute",
                      left: "18px",
                      top: "9px",
                    }}
                  ></div>
                </div>
              )}
              {qbData?.quickbooks_enabled && (
                <div
                  ref={qbPopoverRef}
                  className={`${styles.qbPopoverHost} ${qbPopoverOpen ? styles.qbPopoverOpen : ""}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setQbPopoverOpen(false);
                  }}
                >
                  <div
                    className={styles.bellIconContainer}
                    role='button'
                    tabIndex={-1}
                    aria-expanded={isMobile ? qbPopoverOpen : undefined}
                    aria-haspopup={isMobile ? "dialog" : undefined}
                    onClick={(e) => {
                      if (!isMobile) return;
                      e.stopPropagation();
                      setQbPopoverOpen((open) => !open);
                    }}
                  >
                    <img
                      src={
                        qbData?.quickbooks_connected
                          ? QBConnectedIcon
                          : QBNotConnectedIcon
                      }
                      width='25'
                      height='25'
                      alt={
                        qbData?.quickbooks_connected
                          ? "QuickBooks connected"
                          : "QuickBooks not connected"
                      }
                    />
                  </div>
                  <div
                    className={styles.qbPopover}
                    role='dialog'
                    aria-label='QuickBooks connection'
                  >
                    <div className={styles.qbPopoverInner}>
                      <div
                        className={`${styles.qbPopoverRowAligned} ${styles.qbPopoverStatusRow}`}
                      >
                        <div className={styles.qbPopoverHeaderLine}>
                          <span className={styles.qbPopoverLabel}>
                            QuickBooks Online :
                          </span>
                          <span className={styles.qbStatusCluster}>
                            <span
                              className={
                                qbData?.quickbooks_connected
                                  ? styles.qbStatusDot
                                  : styles.qbStatusDotDisconnected
                              }
                            />
                            <span className={styles.qbPopoverStatusWord}>
                              {qbData?.quickbooks_connected
                                ? "Connected"
                                : "Disconnected"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className={styles.qbPopoverDivider} />
                      <div
                        className={`${styles.qbPopoverRowAligned} ${styles.qbPopoverSyncRow}`}
                      >
                        <div className={styles.qbPopoverIconSlot}>
                          <img
                            src={LastSynced}
                            alt=''
                            className={styles.qbPopoverItemIcon}
                          />
                        </div>
                        <span className={styles.qbPopoverBodyText}>
                          Last Synced : {formatQbRelativeSync(qbData)}
                        </span>
                      </div>
                      <div className={styles.qbPopoverRowAligned}>
                        <div className={styles.qbPopoverIconSlot}>
                          <img
                            src={ViewDetails}
                            alt=''
                            className={styles.qbPopoverItemIcon}
                          />
                        </div>
                        <button
                          type='button'
                          className={styles.qbPopoverAction}
                          onClick={() => {
                            setQbPopoverOpen(false);
                            navigate("/client/settings", {
                              state: { quickbooks_settings: true },
                            });
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div
                className={styles.profileContainer}
                onClick={() =>
                  navigate(
                    isDepositUser ? "/sales_user/profile" : "/client/profile"
                  )
                }
              >
                <div className={styles.initialsContainer}>
                  {getUserInitials(user)}
                </div>
                <div className={styles.userInfoContainer}>
                  <span className={styles.initialsName}>
                    {getFullName(user)}
                  </span>
                  <span className={styles.initialsRole}>
                    {getUserRole(user)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </header>
      {renderMobileDrawer()}

      {!isDepositUser && isNotificationModalOpen && (
        <NotificationsModal
          isModalOpen={isNotificationModalOpen}
          setIsModalOpen={setIsNotificationModalOpen}
          user={user}
        />
      )}
    </>
  );
};

const getFullName = (user) =>
  `${user.first_name || ""} ${user.last_name || ""}`?.trim();
const getUserInitials = (user) =>
  `${user.first_name?.charAt(0) || ""}${
    user.last_name?.charAt(0) || ""
  }`.toUpperCase();
const getUserRole = (user) => {
  if (user.openid_id != null) {
    return "Owner";
  } else if (!user.is_employee && user.client_admin) {
    return "Admin";
  } else {
    return "User";
  }
};

AppClientHeader.propTypes = {
  user: PropTypes.object.isRequired,
  disableNav: PropTypes.bool,
  hideHeadNavOpt: PropTypes.bool,
};

export default AppClientHeader;
